/**
 * Step 5 - Aprovação explícita.
 *
 * Mostra contadores do dry-run e exige clique do usuário para enfileirar.
 * Permite baixar CSV com itens inválidos/duplicados/blacklist para correção offline
 * e exibe prévia paginada das linhas com problema (50 por página, "Carregar mais").
 *
 * CHANGELOG:
 *   RELEASE: 2026-05-17 v1.3.0 - Download de erros agora pagina via
 *            fetchAllJobItems para superar o limite default de 1000 rows
 *            do PostgREST. Exibe o total real (counters.invalid_count +
 *            duplicate_count + blacklisted_count) no botão.
 *   RELEASE: 2026-05-17 v1.2.1 - Removido max-h-[420px] da tabela; scroll
 *            agora é delegado ao wrapper externo do wizard (Step 5 usa
 *            overflow-y-auto) para evitar que a seção fique escondida.
 *   RELEASE: 2026-05-16 v1.2.0 - Adicionada seção colapsável "Linhas com problema"
 *            com tabela paginada (50/página) consumindo RPC
 *            get_deal_import_invalid_items via hook useInvalidItems.
 *   RELEASE: 2026-05-16 v1.1.0 - Botão "Baixar erros" gera CSV das linhas
 *            inválidas/duplicadas/blacklist com motivo da rejeição.
 *   RELEASE: 2026-05-16 v1.0.0 - Versão inicial.
 */

import { useState, useMemo } from 'react';
import { AlertCircle, Download, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from '@/hooks/use-toast';
import { Stat } from './Stat';
import type { ValidationCounters } from './types';
import { useDealImport } from '@/hooks/useDealImport';

interface Step5ApprovalProps {
  counters: ValidationCounters;
  jobId: string | null;
}

// Mapeia status → variante visual do Badge
function statusVariant(status: string): 'destructive' | 'secondary' | 'outline' {
  if (status === 'invalid' || status === 'blacklisted') return 'destructive';
  if (status === 'duplicate') return 'secondary';
  return 'outline';
}

// Traduz o status para label amigável em PT-BR
function statusLabel(status: string): string {
  switch (status) {
    case 'invalid':
      return 'Inválida';
    case 'duplicate':
      return 'Duplicada';
    case 'blacklisted':
      return 'Blacklist';
    default:
      return status;
  }
}

// Extrai motivo legível de validation_errors (jsonb array) ou last_error
function extractReason(validationErrors: any, lastError: string | null): string {
  if (Array.isArray(validationErrors) && validationErrors.length > 0) {
    return validationErrors
      .map((e: any) => {
        if (typeof e === 'string') return e;
        const field = e?.field ? `[${e.field}] ` : '';
        return `${field}${e?.message ?? JSON.stringify(e)}`;
      })
      .join(' • ');
  }
  if (validationErrors && typeof validationErrors === 'object') {
    return JSON.stringify(validationErrors);
  }
  if (typeof validationErrors === 'string') return validationErrors;
  return lastError ?? '—';
}

export function Step5Approval({ counters, jobId }: Step5ApprovalProps) {
  const hasValid = counters.valid_count > 0;
  const rejectedCount =
    counters.invalid_count + counters.duplicate_count + counters.blacklisted_count;
  const hasRejected = rejectedCount > 0;

  const api = useDealImport();

  // Paginação acumulativa: cada clique em "Carregar mais 50" aumenta o limite
  const [pageSize] = useState(50);
  const [pages, setPages] = useState(1);
  const limit = pageSize * pages;

  // Prévia paginada (RPC nova) - aberta por padrão quando há rejeições
  const [open, setOpen] = useState(hasRejected);
  const invalidQuery = api.useInvalidItems(jobId, {
    limit,
    offset: 0,
    enabled: hasRejected && open,
  });

  // Estado local do download (paginado sob demanda)
  const [isDownloading, setIsDownloading] = useState(false);

  const previewItems = invalidQuery.data?.items ?? [];
  const previewTotal = invalidQuery.data?.total ?? rejectedCount;
  const canLoadMore = previewItems.length < previewTotal;

  // Memoiza colunas de problema para tabela
  const tableRows = useMemo(() => previewItems, [previewItems]);

  /**
   * Baixa CSV de TODAS as linhas problemáticas paginando via fetchAllJobItems.
   * Resolve o limite default de 1000 rows do PostgREST que truncava o
   * download (ex.: 14.271 inválidas mostrando apenas 1.000).
   */
  async function downloadErrorsCsv() {
    if (!jobId || rejectedCount === 0) return;
    setIsDownloading(true);
    try {
      const items = await api.fetchAllJobItems(
        jobId,
        ['invalid', 'duplicate', 'blacklisted'],
      );
      if (items.length === 0) {
        toast({ title: 'Nenhuma linha para exportar' });
        return;
      }

      const cols = new Set<string>();
      items.forEach((it) => Object.keys(it.raw_row ?? {}).forEach((k) => cols.add(k)));
      const headers = ['linha', 'status', 'motivo', ...Array.from(cols)];

      const esc = (v: unknown) => {
        const s = v == null ? '' : String(v);
        return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };

      const rows = items.map((it) => {
        const reason = extractReason(it.validation_errors, it.last_error);
        const base = [it.row_index, it.status, reason];
        const extras = Array.from(cols).map((c) => (it.raw_row as any)?.[c]);
        return [...base, ...extras].map(esc).join(';');
      });

      const csv = [headers.join(';'), ...rows].join('\n');
      const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `importacao-erros-${jobId.slice(0, 8)}.csv`;
      a.click();
      URL.revokeObjectURL(url);

      toast({
        title: 'CSV exportado',
        description: `${items.length.toLocaleString('pt-BR')} linha(s) exportadas com motivo da rejeição.`,
      });
    } catch (err: any) {
      toast({
        title: 'Falha ao exportar CSV',
        description: err?.message ?? 'Erro inesperado ao paginar os itens.',
        variant: 'destructive',
      });
    } finally {
      setIsDownloading(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="rounded-lg border p-6 space-y-3">
        <h3 className="font-semibold text-lg">Resumo da validação</h3>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <Stat label="Total de linhas" value={counters.total_rows} />
          <Stat label="Válidas (serão importadas)" value={counters.valid_count} variant="success" />
          <Stat label="Duplicadas (puladas)" value={counters.duplicate_count} variant="warning" />
          <Stat label="Inválidas (não importadas)" value={counters.invalid_count} variant="destructive" />
          <Stat label="Blacklist LGPD" value={counters.blacklisted_count} variant="destructive" />
        </div>

        {hasRejected && jobId && (
          <div className="pt-2 border-t">
            <Button
              variant="outline"
              size="sm"
              onClick={downloadErrorsCsv}
              disabled={isDownloading || rejectedCount === 0}
            >
              <Download className="h-4 w-4 mr-2" />
              {isDownloading
                ? 'Gerando CSV…'
                : `Baixar CSV de erros (${rejectedCount.toLocaleString('pt-BR')})`}
            </Button>
            <p className="text-xs text-muted-foreground mt-1">
              Exporta as linhas que não serão importadas com o motivo da rejeição,
              para correção offline e nova tentativa.
            </p>
          </div>
        )}
      </div>

      {/* Prévia paginada das linhas com problema */}
      {hasRejected && jobId && (
        <Collapsible open={open} onOpenChange={setOpen}>
          <div className="rounded-lg border">
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/40 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-destructive" />
                  <span className="font-medium text-sm">
                    Linhas com problema ({rejectedCount.toLocaleString('pt-BR')})
                  </span>
                </div>
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`}
                />
              </button>
            </CollapsibleTrigger>

            <CollapsibleContent>
              <div className="border-t">
                {invalidQuery.isLoading && previewItems.length === 0 ? (
                  <div className="p-6 text-center text-sm text-muted-foreground">
                    Carregando linhas com problema…
                  </div>
                ) : invalidQuery.isError ? (
                  <div className="p-6 text-center text-sm text-destructive">
                    Erro ao carregar prévia: {(invalidQuery.error as Error)?.message}
                  </div>
                ) : previewItems.length === 0 ? (
                  <div className="p-6 text-center text-sm text-muted-foreground">
                    Nenhuma linha problemática encontrada.
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader className="sticky top-0 bg-background z-10">
                          <TableRow>
                            <TableHead className="w-20"># Linha</TableHead>
                            <TableHead className="w-28">Status</TableHead>
                            <TableHead>Motivo</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {tableRows.map((it) => (
                            <TableRow key={it.id}>
                              <TableCell className="font-mono text-xs">
                                {it.row_index}
                              </TableCell>
                              <TableCell>
                                <Badge variant={statusVariant(it.status)}>
                                  {statusLabel(it.status)}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-xs text-muted-foreground">
                                {extractReason(it.validation_errors, it.last_error)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>

                    <div className="flex items-center justify-between px-4 py-3 border-t bg-muted/20">
                      <span className="text-xs text-muted-foreground">
                        Exibindo {previewItems.length.toLocaleString('pt-BR')} de{' '}
                        {previewTotal.toLocaleString('pt-BR')}
                      </span>
                      {canLoadMore && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPages((p) => p + 1)}
                          disabled={invalidQuery.isFetching}
                        >
                          {invalidQuery.isFetching
                            ? 'Carregando…'
                            : `Carregar mais ${pageSize}`}
                        </Button>
                      )}
                    </div>
                  </>
                )}
              </div>
            </CollapsibleContent>
          </div>
        </Collapsible>
      )}

      {!hasValid ? (
        <div className="flex items-start gap-2 rounded-lg border border-destructive bg-destructive/5 p-4">
          <AlertCircle className="h-5 w-5 text-destructive shrink-0" />
          <p className="text-sm">Nenhuma linha válida encontrada. Revise o CSV e tente novamente.</p>
        </div>
      ) : (
        <div className="text-sm text-muted-foreground">
          <span className="text-foreground font-medium">
            {counters.valid_count.toLocaleString('pt-BR')} negócios
          </span>{' '}
          serão importados em segundo plano. Acompanhe o progresso na próxima tela.
        </div>
      )}
    </div>
  );
}
